import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React from "react";
import { state, ui } from "../../../interfaces/interfaces";
import useDebugInformation from "../../../hooks/useDebugInformation";
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import { useAppSelector } from "@store/hooks";

const name = "AppointmentTabHeader";

const a11yProps = (index: number) => {
  return {
    id: `action-tab-${index}`,
    "aria-controls": `action-tabpanel-${index}`,
  };
}

const handleChange = (event: React.SyntheticEvent<Element, Event>, value: any, affix: "Address" | "Contact") => {
  ui[`ProblemActive${affix}Tab`] = value
};

// arrow function returning icon component for each tab by index
const getIcon = (index: number, tab: any, affix: string) => {

  // index 0 is the first tab, so we need to check if the affix is "Contact" or "Address" as these are the only two tabs that are from the first tab
  switch (index) {
    case 0:
      switch (affix) {
        case "Contact":
          return <LooksOneIcon sx={{ color: (theme) => state.PropertyContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
        case "Address":
          return <LooksOneIcon sx={{ color: (theme) => state.PropertyAddressValid ? theme.palette.success.main : theme.palette.error.main }} />;
        default:
          return <LooksOneIcon sx={{ color: (theme) => state.PropertyContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
      }
    // index 1 is the second tab, so we need to check if the affix is "Landlord" or "Related" as these are the only two tabs that are from the second tab
    case 1:
      switch (affix) {
        case "Contact":
          switch (tab.label) {
            case "Landlord":
              return <LooksTwoIcon sx={{ color: (theme) => state.LandlordContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
            case "Related":
              return <LooksTwoIcon sx={{ color: (theme) => state.RelatedContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
            default:
              return <LooksTwoIcon sx={{ color: (theme) => state.LandlordContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
          }
        default:
          return <LooksTwoIcon sx={{ color: (theme) => state.LandlordContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
      }
    default:
      return <LooksOneIcon sx={{ color: (theme) => state.PropertyContactValid ? theme.palette.success.main : theme.palette.error.main }} />;
  }
}

type AppointmentTabHeaderProps = {
  parent: string
  affix: "Address" | "Contact"
}

const AppointmentTabHeader: React.FC<AppointmentTabHeaderProps> = ({ parent, affix }) => {
  const problemActiveTab = useAppSelector(({ ui }) => ui[`ProblemActive${affix}Tab`])
  const problemTabsList = useAppSelector(({ ui }) => ui.ProblemTabsList)

  // validations of the form PROPERT CONTACT | RELATED CONTACT | LANDLORD CONTACT
  const isPropertyFormValid = useAppSelector(({ state }) => state.PropertyContactValid)
  const isRelatedFormValid = useAppSelector(({ state }) => state.RelatedContactValid)
  const isLandlordFormValid = useAppSelector(({ state }) => state.LandlordContactValid)
  const isAddressFormValid = useAppSelector(({ state }) => state.PropertyAddressValid)

  const debugName = `${parent}▷[${name}]`

  useDebugInformation(debugName, { problemActiveTab, problemTabsList, affix, isPropertyFormValid, isRelatedFormValid, isLandlordFormValid, isAddressFormValid })
  return (
    <AppBar position="static" color="default" >
      <Tabs
        value={problemActiveTab}
        onChange={(e, v) => handleChange(e, v, affix)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        aria-label="action tabs example"
        sx={{ minHeight: "unset" }}
      >
        {JSON.parse(problemTabsList).filter((t: { name: string; }) => ((t.name === "property" && affix === "Address") || affix === "Contact")).map((tab: any, idx: number) => (
          <Tab key={idx} label={`${tab.label} ${affix}`} {...a11yProps(idx)} icon={getIcon(idx, tab, affix)} iconPosition="start" sx={{ minHeight: "unset" }} />)
        )}
      </Tabs>
    </AppBar>
  )
}

AppointmentTabHeader.displayName = name;


export default React.memo(AppointmentTabHeader)